package com.evoapps.evoshift.ui.roster

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Send
import androidx.compose.material.icons.filled.Email
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.di.diViewModel
import com.evoapps.evoshift.domain.model.DayRange
import com.evoapps.evoshift.ui.reports.PrintPerson
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.RosterMailViewModel
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogWidth
import com.evoapps.evoshift.widgets.ErrorOnNavy
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoDropdown
import com.evoapps.evoshift.widgets.EvoSegmented
import com.evoapps.evoshift.ui.LightGold
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.cancel
import evoshift.composeapp.generated.resources.done
import evoshift.composeapp.generated.resources.email_roster
import evoshift.composeapp.generated.resources.email_roster_checking
import evoshift.composeapp.generated.resources.email_roster_days
import evoshift.composeapp.generated.resources.email_roster_everyone
import evoshift.composeapp.generated.resources.email_roster_from_day
import evoshift.composeapp.generated.resources.email_roster_hint
import evoshift.composeapp.generated.resources.email_roster_one
import evoshift.composeapp.generated.resources.email_roster_section_range
import evoshift.composeapp.generated.resources.email_roster_section_who
import evoshift.composeapp.generated.resources.email_roster_person
import evoshift.composeapp.generated.resources.email_roster_send
import evoshift.composeapp.generated.resources.email_roster_to_day
import evoshift.composeapp.generated.resources.email_roster_whole_month
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import org.jetbrains.compose.resources.stringResource

/** Days of [month] in [year]. */
internal fun daysInMonth(year: Int, month: Int): Int =
    LocalDate(year, month, 1).plus(1, DateTimeUnit.MONTH).minus(1, DateTimeUnit.DAY).day

/**
 * "Email the roster" (SH-45): a published month to the whole team or one person, for the month or
 * some days of it (SH-229: two labelled segmented choices, "Send to" and "Period"). Each person gets the team's week tables with their own shifts highlighted, an
 * .ics and the PDF (functions/scheduleMail.js). After sending it says how many e-mails went out.
 */
@Composable
fun EmailRosterDialog(rosterId: String, year: Int, month: Int, people: List<PrintPerson>, onDismiss: () -> Unit) {
    val viewModel: RosterMailViewModel = diViewModel()
    val state by viewModel.state.collectAsState()
    LaunchedEffect(Unit) { viewModel.reset() }
    var everyone by remember { mutableStateOf(true) }
    var person by remember { mutableStateOf(people.firstOrNull()) }
    var wholeMonth by remember { mutableStateOf(true) }
    val lastDay = remember(year, month) { daysInMonth(year, month) }
    var fromDay by remember { mutableStateOf(1) }
    var toDay by remember { mutableStateOf(lastDay) }
    val sent = state.sent.isNotEmpty()

    EvoDialog(
        title = stringResource(Res.string.email_roster),
        icon = Icons.Default.Email,
        onDismiss = onDismiss,
        width = DialogWidth.Compact,
        dismissible = !state.sending,
        confirm = if (sent) {
            DialogAction(stringResource(Res.string.done), onDismiss)
        } else {
            DialogAction(
                stringResource(Res.string.email_roster_send),
                {
                    viewModel.send(
                        rosterId,
                        userId = person?.id.takeIf { !everyone },
                        days = DayRange(fromDay, toDay).takeIf { !wholeMonth }
                    )
                },
                enabled = everyone || person != null,
                loading = state.sending,
                icon = Icons.AutoMirrored.Filled.Send
            )
        },
        dismiss = if (sent) null else DialogAction(stringResource(Res.string.cancel), onDismiss, enabled = !state.sending),
        bodyArrangement = Arrangement.spacedBy(8.dp)
    ) {
        if (sent) {
            state.sent.forEach { line ->
                Text(line.asString(), color = Color.White, style = MaterialTheme.typography.bodyLarge)
            }
            // SH-242: the delivery is checked for about half a minute; the dialog may be closed.
            if (state.checkingDelivery) {
                Text(
                    stringResource(Res.string.email_roster_checking),
                    color = Color.White.copy(alpha = 0.7f),
                    style = MaterialTheme.typography.bodySmall
                )
            }
        } else {
            Text(
                stringResource(Res.string.email_roster_hint),
                color = Color.White.copy(alpha = 0.7f),
                style = MaterialTheme.typography.bodySmall,
                modifier = Modifier.padding(bottom = 4.dp)
            )
            // SH-229: two labelled choices ("Do kogo", "Zakres"), each a segmented pair with its
            // details right under it, instead of four radio rows that read as one list.
            SectionLabel(stringResource(Res.string.email_roster_section_who))
            val everyoneLabel = stringResource(Res.string.email_roster_everyone)
            val oneLabel = stringResource(Res.string.email_roster_one)
            EvoSegmented(
                options = listOf(true, false),
                selected = everyone,
                onSelect = { everyone = it },
                optionLabel = { if (it) everyoneLabel else oneLabel }
            )
            if (!everyone) {
                EvoDropdown(
                    label = stringResource(Res.string.email_roster_person),
                    options = people,
                    selected = person,
                    onSelected = { person = it },
                    optionLabel = { it.name },
                    modifier = Modifier.padding(top = 4.dp)
                )
            }
            SectionLabel(stringResource(Res.string.email_roster_section_range))
            val monthLabel = stringResource(Res.string.email_roster_whole_month)
            val daysLabel = stringResource(Res.string.email_roster_days)
            EvoSegmented(
                options = listOf(true, false),
                selected = wholeMonth,
                onSelect = { wholeMonth = it },
                optionLabel = { if (it) monthLabel else daysLabel }
            )
            if (!wholeMonth) {
                Row(
                    Modifier.fillMaxWidth().padding(top = 4.dp),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    EvoDropdown(
                        label = stringResource(Res.string.email_roster_from_day),
                        options = (1..lastDay).toList(),
                        selected = fromDay,
                        onSelected = { fromDay = it; if (toDay < it) toDay = it },
                        optionLabel = { it.toString() },
                        modifier = Modifier.weight(1f)
                    )
                    EvoDropdown(
                        label = stringResource(Res.string.email_roster_to_day),
                        options = (fromDay..lastDay).toList(),
                        selected = toDay,
                        onSelected = { toDay = it },
                        optionLabel = { it.toString() },
                        modifier = Modifier.weight(1f)
                    )
                }
            }
        }
        state.error?.let {
            Text(it.asString(), color = ErrorOnNavy, style = MaterialTheme.typography.bodySmall, modifier = Modifier.padding(top = 8.dp))
        }
    }
}

/** SH-229: a section's small gold heading in the dialog. */
@Composable
private fun SectionLabel(text: String) {
    Text(
        text,
        color = LightGold,
        style = MaterialTheme.typography.labelLarge,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier.padding(top = 8.dp).semantics { heading() }
    )
}
